import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Globe, ArrowRight } from "lucide-react";
import { SITE } from "@/data/site";
import { getServices } from "@/data/services";
import { getAreas } from "@/data/areas";
import { useLang } from "@/lib/i18n";

const baseLink = "relative px-3 py-2 text-sm font-semibold transition-colors";
const activeLink = "text-brand-navy";
const inactiveLink = "text-slate-700 hover:text-brand-navy";

function NavItem({ to, end, children, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `${baseLink} ${isActive ? activeLink : inactiveLink} ${isActive ? "after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:bg-brand-yellow after:rounded-full" : ""}`
      }
    >
      {children}
    </NavLink>
  );
}

// Tiny per-link tagline shown in the mega menu (Stone-Systems-style sub-copy).
const SERVICE_TAGLINES_EN = {
  "water-heater": "Tank, tankless & hybrid installs",
  "backflow": "Annual cert, repair & install",
  "sewer-lines": "Camera, jet & trenchless repair",
  "faucet": "Fix, rebuild or full replacement",
  "septic-drain": "Tanks, drain fields & service",
  "shut-off-valve": "Quarter-turn upgrades, main valves",
  "toilet": "Repairs, replacements & bidets",
  "drain-cleaning": "Snake, jet & root cutting",
  "remodel-new-construction": "Rough-ins, GC work & buildouts",
};
const SERVICE_TAGLINES_ES = {
  "water-heater": "Con tanque, sin tanque e híbridos",
  "backflow": "Certificación, reparación e instalación",
  "sewer-lines": "Cámara, jet y sin zanja",
  "faucet": "Arreglo, reconstrucción o reemplazo",
  "septic-drain": "Tanques, campos de drenaje y servicio",
  "shut-off-valve": "Válvulas de cuarto de vuelta y principales",
  "toilet": "Reparación, reemplazo y bidés",
  "drain-cleaning": "Sonda, hidrojet y corte de raíces",
  "remodel-new-construction": "Rough-ins, trabajo con GCs y obras",
};

const AREA_TAGLINES_EN = {
  "palm-beach-county": "HQ · same-day in most cities",
  "broward-county": "Fort Lauderdale to Hollywood",
  "miami-dade-county": "Miami metro, Coral Gables, Doral",
  "martin-county": "Stuart, Jensen Beach, Palm City",
};
const AREA_TAGLINES_ES = {
  "palm-beach-county": "Sede · el mismo día en casi todo",
  "broward-county": "De Fort Lauderdale a Hollywood",
  "miami-dade-county": "Área de Miami, Coral Gables, Doral",
  "martin-county": "Stuart, Jensen Beach, Palm City",
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [mServicesOpen, setMServicesOpen] = useState(false);
  const [mAreasOpen, setMAreasOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  const SERVICES = getServices(lang);
  const AREAS = getAreas(lang);
  const SERVICE_TAG = lang === "es" ? SERVICE_TAGLINES_ES : SERVICE_TAGLINES_EN;
  const AREA_TAG = lang === "es" ? AREA_TAGLINES_ES : AREA_TAGLINES_EN;
  const toggleLang = () => setLang(lang === "en" ? "es" : "en");

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/95 border-b border-slate-200"
    >
      <div className="hidden md:block bg-brand-navy-dark text-white text-xs">
        <div className="container-max flex items-center justify-between py-2">
          <p className="font-medium">
            {lang === "es"
              ? "Emergencia 24/7 · Licenciados y Asegurados · 12 Años en el Sur de Florida"
              : "24/7 Emergency · Licensed & Insured · 12 Years in South Florida"}
          </p>
          <div className="flex items-center gap-4">
            <a
              data-testid="header-strip-call"
              href={`tel:${SITE.primaryPhone}`}
              className="font-semibold text-brand-yellow hover:text-white transition-colors"
            >
              {t.cta.call} {SITE.primaryPhone}
            </a>
            <span className="opacity-50">|</span>
            <a href={`mailto:${SITE.email}`} className="hover:text-brand-yellow transition-colors">
              {SITE.email}
            </a>
            <span className="opacity-50">|</span>
            <button
              data-testid="lang-toggle-desktop"
              onClick={toggleLang}
              className="inline-flex items-center gap-1 hover:text-brand-yellow transition-colors font-semibold"
              aria-label="Toggle language"
            >
              <Globe size={12} />
              {lang === "en" ? "Español" : "English"}
            </button>
          </div>
        </div>
      </div>

      <div className="container-max flex items-center justify-between py-3 gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <img
            src={SITE.logo}
            alt="Faster Plumbing"
            className="h-14 w-14 object-contain"
            loading="eager"
          />
          <div className="hidden sm:block leading-tight">
            <p className="font-heading text-lg font-bold text-brand-navy tracking-tight">
              Faster Plumbing
            </p>
            <p className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-semibold">
              {t.common.brandTagline}
            </p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavItem to="/" end testId="nav-home">{t.nav.home}</NavItem>

          {/* Services mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <NavLink
              to="/services"
              data-testid="nav-services"
              className={({ isActive }) =>
                `${baseLink} inline-flex items-center gap-1 ${isActive ? activeLink : inactiveLink} ${isActive ? "after:absolute after:left-3 after:right-7 after:-bottom-0.5 after:h-0.5 after:bg-brand-yellow after:rounded-full" : ""}`
              }
            >
              {t.nav.services}{" "}
              <ChevronDown
                size={14}
                className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
              />
            </NavLink>
            {servicesOpen && (
              <div
                data-testid="services-mega-menu"
                className="absolute left-0 top-full pt-3 w-[820px] -translate-x-8"
              >
                <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl shadow-slate-900/15 overflow-hidden grid grid-cols-[1.6fr_1fr]">
                  {/* Service columns */}
                  <div className="p-6 grid grid-cols-2 gap-1">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="group block px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                      >
                        <p className="font-heading text-[15px] font-semibold text-slate-900 group-hover:text-brand-navy">
                          {s.name}
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5 leading-tight">
                          {SERVICE_TAG[s.slug] || ""}
                        </p>
                      </Link>
                    ))}
                  </div>

                  {/* Feature card */}
                  <div className="bg-gradient-to-br from-brand-navy via-brand-navy-dark to-slate-900 text-white p-7 flex flex-col">
                    <span className="inline-flex items-center gap-1.5 bg-brand-yellow text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md w-fit">
                      {t.common.freeEstimateTag}
                    </span>
                    <h3 className="font-heading text-xl font-bold mt-3 leading-tight">
                      {t.common.featuredCta}
                    </h3>
                    <p className="text-sm text-white/75 mt-2 leading-relaxed">
                      {t.common.featuredCtaSub}
                    </p>
                    <div className="mt-auto pt-6 space-y-2">
                      <a
                        href={`tel:${SITE.primaryPhone}`}
                        className="flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-2.5 px-4 rounded-xl whitespace-nowrap text-sm"
                      >
                        <Phone size={15} />
                        {SITE.primaryPhone}
                      </a>
                      <Link
                        to="/services"
                        className="flex items-center justify-center gap-1.5 border border-white/30 hover:bg-white/10 text-white font-semibold py-2.5 px-4 rounded-xl text-sm transition-colors"
                      >
                        {t.cta.allServices}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Service Areas mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => setAreasOpen(true)}
            onMouseLeave={() => setAreasOpen(false)}
          >
            <NavLink
              to="/service-areas"
              data-testid="nav-service-areas"
              className={({ isActive }) =>
                `${baseLink} inline-flex items-center gap-1 ${isActive ? activeLink : inactiveLink} ${isActive ? "after:absolute after:left-3 after:right-7 after:-bottom-0.5 after:h-0.5 after:bg-brand-yellow after:rounded-full" : ""}`
              }
            >
              {t.nav.areas}{" "}
              <ChevronDown
                size={14}
                className={`transition-transform ${areasOpen ? "rotate-180" : ""}`}
              />
            </NavLink>
            {areasOpen && (
              <div
                data-testid="areas-mega-menu"
                className="absolute left-0 top-full pt-3 w-[620px] -translate-x-8"
              >
                <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl shadow-slate-900/15 overflow-hidden grid grid-cols-[1.4fr_1fr]">
                  <div className="p-6 grid grid-cols-1 gap-1">
                    {AREAS.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/service-areas/${a.slug}`}
                        data-testid={`nav-area-${a.slug}`}
                        className="group block px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                      >
                        <p className="font-heading text-[15px] font-semibold text-slate-900 group-hover:text-brand-navy">
                          {a.name}
                        </p>
                        <p className="text-[12px] text-slate-500 mt-0.5 leading-tight">
                          {AREA_TAG[a.slug] || ""}
                        </p>
                      </Link>
                    ))}
                  </div>
                  <div className="bg-gradient-to-br from-brand-yellow to-amber-400 text-slate-900 p-7 flex flex-col">
                    <span className="inline-flex items-center gap-1.5 bg-slate-900 text-brand-yellow text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md w-fit">
                      {t.common.brandTagline}
                    </span>
                    <h3 className="font-heading text-xl font-bold mt-3 leading-tight">
                      {t.common.featuredCta}
                    </h3>
                    <p className="text-sm text-slate-800 mt-2 leading-relaxed">
                      {lang === "es"
                        ? "Cuatro condados, un solo equipo, los mismos estándares."
                        : "Four counties, one crew, same standards."}
                    </p>
                    <Link
                      to="/service-areas"
                      className="mt-auto pt-6 inline-flex items-center gap-1.5 font-bold text-slate-900 hover:gap-2.5 transition-all"
                    >
                      {t.cta.viewAllAreas} <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavItem to="/about" testId="nav-about">{t.nav.about}</NavItem>
          <NavItem to="/contact" testId="nav-contact">{t.nav.contact}</NavItem>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="header-call-cta"
            href={`tel:${SITE.primaryPhone}`}
            className="inline-flex items-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-2.5 px-4 rounded-xl transition-colors whitespace-nowrap shadow-sm"
          >
            <Phone size={16} />
            <span>{SITE.primaryPhone}</span>
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggleLang}
            className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold whitespace-nowrap"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            {lang === "en" ? "ES" : "EN"}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200"
            onClick={() => setOpen(!open)}
            aria-label="Open menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-72px)] overflow-y-auto overscroll-contain"
          data-testid="mobile-menu"
        >
          <div className="container-max py-3 space-y-1">
            {/* Switch language banner (full text, clear context) */}
            <button
              data-testid="lang-toggle-mobile-banner"
              onClick={toggleLang}
              className="w-full flex items-center justify-center gap-2 py-2.5 mb-1 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-700"
            >
              <Globe size={15} />
              {lang === "en" ? "Switch to Español" : "Cambiar a English"}
            </button>

            <MobileLink to="/" testId="m-nav-home" onClick={() => setOpen(false)}>
              {t.nav.home}
            </MobileLink>

            {/* Collapsible Services */}
            <div className="rounded-xl">
              <div className="flex items-center justify-between">
                <NavLink
                  to="/services"
                  data-testid="m-nav-services"
                  end={false}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex-1 py-2.5 text-base font-semibold ${isActive ? "text-brand-navy" : "text-slate-800"}`
                  }
                >
                  {t.nav.services}
                </NavLink>
                <button
                  type="button"
                  data-testid="m-nav-services-toggle"
                  onClick={() => setMServicesOpen((v) => !v)}
                  aria-expanded={mServicesOpen}
                  aria-label="Expand services"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${mServicesOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {mServicesOpen && (
                <div className="pl-4 border-l-2 border-brand-yellow/60 ml-2 mb-2 space-y-0.5">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`m-nav-service-${s.slug}`}
                      className="block py-2 text-sm text-slate-600 hover:text-brand-navy"
                      onClick={() => setOpen(false)}
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Collapsible Service Areas */}
            <div className="rounded-xl">
              <div className="flex items-center justify-between">
                <NavLink
                  to="/service-areas"
                  data-testid="m-nav-areas"
                  end={false}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex-1 py-2.5 text-base font-semibold ${isActive ? "text-brand-navy" : "text-slate-800"}`
                  }
                >
                  {t.nav.areas}
                </NavLink>
                <button
                  type="button"
                  data-testid="m-nav-areas-toggle"
                  onClick={() => setMAreasOpen((v) => !v)}
                  aria-expanded={mAreasOpen}
                  aria-label="Expand service areas"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${mAreasOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {mAreasOpen && (
                <div className="pl-4 border-l-2 border-brand-yellow/60 ml-2 mb-2 space-y-0.5">
                  {AREAS.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/service-areas/${a.slug}`}
                      data-testid={`m-nav-area-${a.slug}`}
                      className="block py-2 text-sm text-slate-600 hover:text-brand-navy"
                      onClick={() => setOpen(false)}
                    >
                      {a.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <MobileLink to="/about" testId="m-nav-about" onClick={() => setOpen(false)}>
              {t.nav.about}
            </MobileLink>
            <MobileLink to="/contact" testId="m-nav-contact" onClick={() => setOpen(false)}>
              {t.nav.contact}
            </MobileLink>

            <a
              data-testid="m-header-call"
              href={`tel:${SITE.primaryPhone}`}
              className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3 px-5 rounded-xl whitespace-nowrap"
              onClick={() => setOpen(false)}
            >
              <Phone size={16} />
              <span>{t.cta.call} {SITE.primaryPhone}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, onClick, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        `block py-2.5 text-base font-semibold ${isActive ? "text-brand-navy" : "text-slate-800"}`
      }
    >
      {children}
    </NavLink>
  );
}
